import { Component, inject, OnInit, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { GatehousePassportCellComponent } from './gatehouse-passport-cell.component';
import { SchedulerStatusTabsComponent } from '../scheduler/scheduler-status-tabs.component';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { PassportService, PassportRecord } from '../shared/passport.service';
import { PassportDetailsStore, type PassportDetails } from '../shared/passport-details.store';
import { UiStateService } from '../shared/ui-state.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { PassportHelperService } from '../shared/passport-helper.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { BaseTabPersonaComponent } from '../shared/base-tab-persona.component';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { NoPassportsComponent } from '../shared/no-passports.component';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { HistoryPanelService } from '../shared/history-panel.service';
import { FormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { PassportsDesktopTableComponent } from '../shared/passports-desktop-table.component';
import { PERSONA_NAVBAR_ITEMS } from '../shared/constants/navbar.constant';
import {
  clearGatehouseListRestoreState,
  normalizeGatehouseDesktopShowRows,
  parseGatehouseListSnapshot,
  peekExpectGatehouseRestore,
  saveGatehouseListSnapshot,
  type GatehouseListSnapshot,
  type GatehouseSchedulerTab
} from './gatehouse-list-state.storage';

interface GatehouseListDerived {
  readonly filteredRecords: PassportRecord[];
  readonly visibleRecords: PassportRecord[];
  readonly counts: {
    readonly all: number;
    readonly load: number;
    readonly shunting: number;
    readonly transloading: number;
    readonly receiving: number;
    readonly transferred: number;
  };
}

type GatehouseSearchView = 'desktop' | 'mobile';

interface GatehouseCounts {
  all: number;
  load: number;
  shunting: number;
  transloading: number;
  receiving: number;
  transferred: number;
}

/**
 * Gatehouse persona component
 * Extends BaseTabPersonaComponent and adds extra passport details fields
 */
@Component({
  selector: 'app-gatehouse',
  standalone: true,
  imports: [
    HeaderComponent, 
    GatehousePassportCellComponent, 
    SchedulerStatusTabsComponent, 
    CommonModule, 
    NavbarComponent, 
    LoadingSpinnerComponent, 
    DateWithOrdinalPipe, 
    NoPassportsComponent,
    FormsModule,
    PassportsDesktopTableComponent
  ],
  templateUrl: './gatehouse.component.html',
  styleUrl: './gatehouse.component.css'
})
export class GatehouseComponent extends BaseTabPersonaComponent implements OnInit {
  private readonly commentsPanelService = inject(CommentsPanelService);
  private readonly historyPanelService = inject(HistoryPanelService);

  private pendingGatehouseRestore: GatehouseListSnapshot | null = null;
  private passportIdToScrollAfterRestore: string | null = null;
  private readonly isDesktopViewSignal = signal(false);
  
  /** Navbar items constant for template */
  readonly navbarItems = PERSONA_NAVBAR_ITEMS;


  // Explicitly define filters to ensure template access
  override filters: Array<{ id: string; label: string; options: string[] }> = [];

  protected get personaName(): string {
    return 'gatehouse';
  }

  protected get defaultPersona(): string {
    return 'gatehouse';
  }

  /** Single pass over records for counts + visible list (search uses debounced `filterQuery`). */
  readonly gatehouseListDerived = computed((): GatehouseListDerived => {
    const records = this.readSignalValue(this.passports.records, []);
    const query = (this.readSignalValue(this.searchService.filterQuery, '') || '').trim().toLowerCase();
    const activeFilters = this.readSignalValue(this.filterService.filters, {});
    const selectedTab = this.readSignalValue(this.ui.schedulerTabSelected, 'all');
    const view: GatehouseSearchView = this.isDesktopViewSignal() ? 'desktop' : 'mobile';
    const filteredRecords: PassportRecord[] = [];
    const visibleRecords: PassportRecord[] = [];
    const counts = this.createGatehouseCounts();

    for (const rec of records) {
      const det = this.detailsStore.get(rec.id);
      if (!this.matchesGatehouseFilters(det, activeFilters)) {
        continue;
      }
      if (!this.matchesGatehouseSearch(rec, det, { query, view })) {
        continue;
      }

      filteredRecords.push(rec);
      const category = this.mapStatusToTab(rec);
      this.updateGatehouseCounts(category, counts);

      if (selectedTab === 'all' || category === selectedTab) {
        visibleRecords.push(rec);
      }
    }

    counts.all = filteredRecords.length;

    return {
      filteredRecords,
      visibleRecords,
      counts
    };
  });


  constructor(
    http: HttpClient,
    searchService: SearchService,
    filterService: FilterService,
    passports: PassportService,
    ui: UiStateService,
    detailsStore: PassportDetailsStore,
    router: Router,
    offlineStorage: OfflineStorageService,
    commentsNotification: CommentsNotificationService,
    helper: PassportHelperService,
    personaRouting: PersonaRoutingService
  ) {
    super(http, searchService, filterService, passports, ui, detailsStore, router, offlineStorage, commentsNotification, helper, personaRouting);
  }

  override ngOnInit(): void {
    let restoring = peekExpectGatehouseRestore();
    let snapshot = restoring ? parseGatehouseListSnapshot() : null;
    if (restoring && !snapshot) {
      clearGatehouseListRestoreState();
      restoring = false;
      snapshot = null;
    }
    this.pendingGatehouseRestore = snapshot;

    if (!restoring) {
      this.filterService.clear();
      this.searchService.clear();
      this.ui.setSearchOpen(false);
      this.ui.setFiltersOpen(false);
    }

    this.filters = this.helper.getDefaultOriginDestinationApnFilters();
    super.ngOnInit();
    this.updateDesktopViewState(true);
  }

  protected override shouldSkipDataReload(): boolean {
    if (peekExpectGatehouseRestore()) {
      return false;
    }
    return super.shouldSkipDataReload();
  }

  private createGatehouseCounts(): GatehouseCounts {
    return {
      all: 0,
      load: 0,
      shunting: 0,
      transloading: 0,
      receiving: 0,
      transferred: 0,
    };
  }

  private matchesGatehouseFilters(details: PassportDetails | undefined, activeFilters: Record<string, string[]>): boolean {
    const apnValue = details?.apnName || details?.apn;
    if (!this.helper.matchesFilter(activeFilters, 'apnName', apnValue)) {
      return false;
    }
    if (!this.helper.matchesFilter(activeFilters, 'haulier', details?.haulier)) {
      return false;
    }
    const destinationValue = details?.destinationWeighbridge || details?.destination;
    if (!this.helper.matchesFilter(activeFilters, 'destination', destinationValue)) {
      return false;
    }
    return this.helper.matchesFilter(activeFilters, 'origin', details?.origin);
  }

  private matchesGatehouseSearch(
    record: PassportRecord,
    details: PassportDetails | undefined,
    options: { query: string; view: GatehouseSearchView }
  ): boolean {
    if (!options.query) {
      return true;
    }
    const baseFields = this.helper.buildSearchFields(record, details);
    if (options.view === 'desktop') {
      if (!details) {
        return false;
      }
      return this.buildDesktopSearchHaystack(baseFields, details).includes(options.query);
    }
    const haystack = baseFields.join(' ').toLowerCase();
    return haystack.includes(options.query);
  }

  private updateGatehouseCounts(category: GatehouseSchedulerTab, counts: GatehouseCounts): void {
    if (category === 'load') {
      counts.load += 1;
    } else if (category === 'shunting') {
      counts.shunting += 1;
    } else if (category === 'transloading') {
      counts.transloading += 1;
    } else if (category === 'receiving') {
      counts.receiving += 1;
    } else if (category === 'transferred') {
      counts.transferred += 1;
    }
  }

  override get allCount(): number {
    return this.gatehouseListDerived().counts.all;
  }

  override get loadCount(): number {
    return this.gatehouseListDerived().counts.load;
  }

  override get shuntingCount(): number {
    return this.gatehouseListDerived().counts.shunting;
  }

  override get transloadingCount(): number {
    return this.gatehouseListDerived().counts.transloading;
  }

  get receivingCount(): number { 
    return this.gatehouseListDerived().counts.receiving;
  }

  override get transferredCount(): number {
    return this.gatehouseListDerived().counts.transferred;
  }
  override get visibleRecords(): PassportRecord[] {
    return this.gatehouseListDerived().visibleRecords;
  }

  private mapStatusToTab(rec: PassportRecord): GatehouseSchedulerTab {
    return rec.tabCategory ?? 'load';
  }

  /**
   * Override to add gatehouse-specific sort columns (passportComplete, status)
   */
  protected override getSortValues(column: string, a: PassportRecord, b: PassportRecord, aDetails: any, bDetails: any): { valA: any; valB: any } {
    // Handle gatehouse-specific columns
    if (column === 'passportComplete') {
      return {
        valA: aDetails?.isPassportCompleted ? 'yes' : 'no',
        valB: bDetails?.isPassportCompleted ? 'yes' : 'no'
      };
    }
    if (column === 'status') {
      return {
        valA: a.status || '',
        valB: b.status || ''
      };
    }
    // Fall back to base class for common columns
    return super.getSortValues(column, a, b, aDetails, bDetails);
  }


  get totalPages(): number {
    return this.getTotalPages(this.visibleRecords.length);
  }

  onTabSelect(tab: 'all' | 'load' | 'shunting' | 'transloading' | 'receiving' | 'transferred'): void {
    this.ui.setSchedulerTabSelected(tab);
    this.currentPage = 1;
    this.rebuildFilters();
  }

  override updateDesktopViewState(force = false): void {
    super.updateDesktopViewState(force);
    this.isDesktopViewSignal.set(this.isDesktopView);
    if (this.isDesktopView) {
      // Ensure we're not using offline storage in desktop
      console.log('Desktop view active - offline storage disabled');
    }
  }
  
  protected shouldUseOfflineFeatures(): boolean {
    // Only use offline features on mobile
    return this.isDesktopView === false;
  }

  openPassportDetails(id: string): void {
    const filtersRaw = this.filterService.filters();
    const filtersClone: Record<string, string[]> = {};
    for (const key of Object.keys(filtersRaw)) {
      const vals = filtersRaw[key];
      filtersClone[key] = vals ? [...vals] : [];
    }

    const scrollY =
      globalThis.window === undefined
        ? undefined
        : globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0;

    saveGatehouseListSnapshot({
      tab: this.ui.schedulerTabSelected(),
      filters: filtersClone,
      searchQuery: this.searchService.query(),
      currentPage: this.currentPage,
      itemsPerPage: normalizeGatehouseDesktopShowRows(this.itemsPerPage),
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      passportId: id,
      scrollY: scrollY === 0 ? undefined : scrollY,
    });

    this.ui.setExpandedRecord(id);
    this.scrollPositionService.saveScrollPosition('/gatehouse');
    const tab = this.ui.schedulerTabSelected();
    void this.router.navigate(['/passport', id], { state: { from: 'gatehouse', tab } });
  }

  private applyGatehouseListRestore(snapshot: GatehouseListSnapshot): void {
    this.filterService.clear();
    this.searchService.clear();
    this.ui.setSchedulerTabSelected(snapshot.tab);
    for (const key of Object.keys(snapshot.filters)) {
      const values = snapshot.filters[key];
      if (values?.length) {
        this.filterService.setFilter(key, values);
      }
    }
    this.searchService.setQuery(snapshot.searchQuery);
    this.searchService.syncFilterQueryWithQuery();
    this.rowsDropdownOpen = false;
    this.itemsPerPage = normalizeGatehouseDesktopShowRows(snapshot.itemsPerPage);
    this.sortColumn = snapshot.sortColumn;
    this.sortDirection = snapshot.sortDirection;
    this.currentPage = snapshot.currentPage;
    const totalPages = this.helper.getTotalPages(this.visibleRecords.length, this.itemsPerPage);
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    this.rebuildFilters();
    this.passportIdToScrollAfterRestore = snapshot.passportId;
    clearGatehouseListRestoreState();
    globalThis.setTimeout(() => {
      this.tryScrollRestoredPassportIntoView();
    }, 250);
  }

  private tryScrollRestoredPassportIntoView(): void {
    const id = this.passportIdToScrollAfterRestore;
    if (!id) {
      return;
    }
    this.passportIdToScrollAfterRestore = null;
    if (globalThis.document === undefined) {
      return;
    }
    requestAnimationFrame(() => {
      const safeId = id.replaceAll('"', '');
      const candidates = globalThis.document.querySelectorAll(`[data-passport-id="${safeId}"]`);
      const el = Array.from(candidates).find(e => (e as HTMLElement).offsetParent !== null);
      el?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }

  /**
   * Build desktop search haystack with additional fields
   */
  private buildDesktopSearchHaystack(baseFields: string[], det: any): string {
    const dueDateTime = det?.dueDateTime || '';
    const formattedScheduled = dueDateTime ? this.dateTimeHelper.formatScheduledText(dueDateTime) : undefined;
    const scheduledText = formattedScheduled ? formattedScheduled.toLowerCase() : '';
    const combinedVendor = `${det?.vendorNumber || ''} - ${det?.vendorName || ''}`.toLowerCase();
    const statusText = (det?.statusCode || '').toLowerCase().replaceAll('_', ' ');

    return [
      ...baseFields,
      scheduledText,
      combinedVendor,
      statusText,
      det?.vendorNumber || '',
      det?.vendorName || '',
      det?.shuntingYardName || '',
      det?.transloadingStationName || ''
    ].join(' ').toLowerCase();
  }

  /**
   * Build header object for comments/history panels
   * Override to add gatehouse-specific fields
   */
  protected override buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string; isGatehouse?: boolean } | undefined {
    const header = super.buildPanelHeader(id);
    if (header) {
      return { ...header, isGatehouse: true };
    }
    return undefined;
  }

  override openComments(id: string, commentsPanelService?: CommentsPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      (commentsPanelService || this.commentsPanelService).openCommentsPanel(id, header);
    }
  }

  override openHistory(id: string, historyPanelService?: HistoryPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      (historyPanelService || this.historyPanelService).openHistoryPanel(id, header);
    }
  }

  /**
   * Override to add gatehouse-specific fields to passport details
   */
  protected override storePassportDetails(passports: PassportMainWithDraftDto[]): void {
    for (const p of passports) {
      this.detailsStore.upsert(this.buildPassportDetails(p));
    }
  }

  /**
   * Build passport details object from API passport data
   */
  private buildPassportDetails(p: PassportMainWithDraftDto): any {
    return {
      id: p.passportID.toString(),
      loadNumber: p.loadNumber || '',
      vendorName: p.vendorName || '',
      vendorNumber: p.vendorNumber || '',
      apn: p.apn || '',
      apnName: p.apnName || '',
      destination: p.destination || '',
      destinationWeighbridge: p.destinationWeighbridge || '',
      origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
      bulkerNumber: p.trailerNumber || '',
      bulkerMain: p.trailerMain || '',
      dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),
      haulier: p.haulierName || '',
      plantID: p.plantID,
      statusCode: p.statusCode || '',
      // Gatehouse-specific fields
      haccpImageLinks: p.haccpImageLinks || [],
      imageCount: p.imageCount || 0,
      deletedImageFilenames: [],  // Clear old editing state when refreshing from API
      haccpImageFiles: [],  // Clear old editing state when refreshing from API
      variety: p.variety || '',
      haccpCheck: p.haccpCheck ? 'Yes' : 'No',
      authorisedBy: p.authorizerName || '',
      loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.loadingDateTime || ''),
      plannedDateTime: p.plannedDateTime ? this.dateTimeHelper.ensureUtcTimestamp(p.plannedDateTime) : undefined,
      shuntingYardID: p.shuntingYardID ?? null,
      shuntingYardName: p.shuntingYardName || '',
      isShuntingRequired: p.isShuntingRequired ?? true,
      transloadingStationID: p.transloadingStationID ?? null,
      hasSeenPcrChanges: p.hasSeenPcrChanges || false,
      isPassportCompleted: p.isPassportCompleted ?? false,
      isCancelled: (p.loadStatusName || '').toLowerCase() === 'cancelled',
      loadStatusName: p.loadStatusName || '',
      loadStatus: p.loadStatus || '',
      transferFlag: p.transferFlag ?? false
    };
  }

  protected override async loadPassportsFromAPI(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    try {
      const userID = localStorage.getItem('userID');
      // FIX: Use component's personaName instead of localStorage to ensure correct roleId
      // This prevents issues when user navigates directly via URL (localStorage might have old persona)
      const persona = this.personaName; // Always use 'gatehouse' for gatehouse component
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      const roleId = this.helper.getRoleIdForPersona(persona);
      console.log(`📋 Gatehouse loading passports with persona: '${persona}', roleId: ${roleId}`);
      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports?userId=${userID}&roleId=${roleId}`;
      const headers = token ? { 'Authorization': `${tokenType} ${token}`, 'Content-Type': 'application/json' } : undefined;
      
      let passports: PassportMainWithDraftDto[] = [];
      if (token && headers) {
        try {
           passports = await firstValueFrom(this.http.get<PassportMainWithDraftDto[]>(apiUrl, { headers }));
        } catch (e) {
           console.warn('API call failed or no data', e);
           passports = [];
        }
      } else {
        console.warn('No access token found, using dummy data only for Gatehouse desktop view');
      }

      const allPassports = passports;

      // Map passports to frontend records with tab category based on shuntingYardID, transloadingStationID, and statusCode
      const frontendRecords: PassportRecord[] = allPassports.map((p: PassportMainWithDraftDto) => {
        const tabCategory = this.helper.mapPassportToTabCategory(
          p.statusCode,
          p.shuntingYardID,
          p.transloadingStationID,
          p.loadStatus,
          p.transferFlag
        );
        
        return {
          id: p.passportID.toString(),
          status: 'pending', 
          isSynced: true,
          tabCategory: tabCategory,
          statusCode: p.statusCode || '',
          transferFlag: p.transferFlag ?? false
        };
      });
      this.passports.records.set(frontendRecords);

      this.storePassportDetails(allPassports);

      if (headers) {
        await this.cacheOfflineData(headers);
      }
      this.rebuildFilters();
      if (this.pendingGatehouseRestore) {
        this.applyGatehouseListRestore(this.pendingGatehouseRestore);
        this.pendingGatehouseRestore = null;
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Override to disable offline caching
   * Offline caching is only enabled for Grower and Transloader personas on mobile view
   */
  protected override async cacheOfflineData(headers: any): Promise<void> {
    // Gatehouse does not support offline caching
    // Offline caching is only for Grower and Transloader personas on mobile view
    return;
  }

  protected override rebuildFilters(): void {
    try {
      // Use all records (not filtered) to ensure filter options remain available
      const records = this.passports.records();
      this.filters = this.helper.buildOriginDestinationApnFilters(records, this.detailsStore);
      console.log('Filters rebuilt (gatehouse desktop):', this.filters);
    } catch (err) {
      console.warn('Error rebuilding filters:', err);
      this.filters = this.helper.getDefaultOriginDestinationApnFilters();
    }
  }
}
